/**
 * Created by PRIME AI.
 * @author Samuel Hinchliffe <samuel@primeai.co.uk, sam.hinchliffe.work@gmail.com>
 * @see    [Linkedin] {@link https://www.linkedin.com/in/samuel-hinchliffe-2bb5801a5/}
 *
 * @summary A custom class for each project, you define the app runner. You define what apps
 * it should run from the AppFactory, as the AppFactory has all the useful functions for
 * running PRIME AI Apps.
 *
 * Created at: 25/10/2023
 * @see       [PRIME-AI GITHUB] {@link https://github.com/PRIME-AI-GITHUB}
 * @see       [Website]         {@link https://www.primeai.co.uk/}
 * @copyright 2018-2023 PRIME AI LTD
 */

import HuggerCTA from '../Components/PhotoHugger/HuggerCTA';
import HuggerRecommendation from '../Components/PhotoHugger/HuggerRecommendation';
import {
    Log, App, AppNotAllowedError, CacheStorage,
} from '../../modules/WidgetWorks/WidgetWorks';

// !! Extend a baseline app.
// !! App = [config, appFactory] | start? | delete | validApp [init, isAllowedToRun] | overRide | unOverride
/// !! hooks | overrideHook | preLoad | postLoad |
// !! RENAME THIS.
export default class PhotoHugger extends App {
    constructor(config, appFactory) {
        super(config, appFactory);
    }

    overrideHook() {}

    preLoad() {}

    postLoad() {
        this.runPostLoadAnalyticsEvent();
    }

    isAppAllowedToRunDueToABTest() {
        if (!this.config.abTest) return true;

        // Does the user already have a ab test flag?
        const flag = localStorage.getItem('PRIME_AI_FCBP_APP_AB_TEST');

        // If the flag is null, it means the AB test is not happening at all.
        if (flag === null || flag === 'false') return false;

        // If the flag is 'true', it means an AB test is going on and the app should not run.
        if (flag === 'true') return true;

        // Enabled in the config and the user has not been assigned a flag.
        if (flag == null) {
            const result = Math.random() < 0.5;
            localStorage.setItem('PRIME_AI_FCBP_APP_AB_TEST', result);
            return result === 'true';
        }

        // Default to true.
        return true;
    }

    async isAppAllowedToRun() {
        if (!this.config.apps.HuggerRecommendation.enabled || !this.config.apps.HuggerCTA.enabled) return { result: false, reason: 'One or more of the components are disabled in the config' };

        // The whitelist is a list of regex patterns that the current URL must match!
        // if no url matches, the app will not run.
        const { whiteList } = this.config.hugger_app;

        // Remove any args if they exist.
        const currentUrl = window.location.href.split('?')[0];

        for (const entry of whiteList) {
            const { pattern } = entry;
            const regex = new RegExp(pattern);
            if (regex.test(currentUrl)) {
                console.log(...Log.w(`${this.constructor.name}`, 'LOG', `AppAllowed. URL matches whitelist: ${currentUrl}`));
                return { result: true, reason: 'The current URL matches the whitelist.' };
            }
        }

        // If we're in a AD Test, we need to check if the app is allowed to run.
        if (!this.isAppAllowedToRunDueToABTest()) return { result: false, reason: 'AB Test is running, this app is not allowed to run due to the flag being false.' };

        return { result: false, reason: 'URL does not match any whitelisted pattern' };
    }

    runViewAnalyticsEvent() {
        // View / display will happen on every page even if the pdp isn't found
        // as well as running a extra event where we check if the user is unique or not

        // Run the view event.
        this.appFactory.analytics.registerEvent('view', 1, 'PhotoHugger');

        // Check if the user is unique.
        const flagExists = CacheStorage.get('PRIME_AI_PHOTO_HUGGER_APP_UNIQUE_USER');

        // If the user is unique, register a unique visitor event.
        // and set a flag to say they are no longer unique.
        if (!flagExists) {
            this.appFactory.analytics.registerEvent('unique_visitors', 1, 'PhotoHugger');
            CacheStorage.set('PRIME_AI_PHOTO_HUGGER_APP_UNIQUE_USER', true, CacheStorage.expirationEndOfDay());
        }
    }

    runPostLoadAnalyticsEvent() {
        // Where-in the button / icon is displayed.
        // As we are on a pdp and have a product, we can run this event.
        // As we can see the button.
        this.appFactory.analytics.registerEvent('button_display', 1, 'PhotoHugger');
    }

    async init() {
        try {
            // Run the over-ride hook.
            this.overrideHook();

            // Run analytics display here.
            // As this is a page view/
            this.runViewAnalyticsEvent();

            const allowedToRun = await this.isAppAllowedToRun();
            if (!allowedToRun.result) throw new Error(allowedToRun.reason);

            // Run analytics here. Page view.
            // View will always run even if the following fails.
            this.appFactory.analytics.registerEvent('view', 1, 'PhotoHugger');

            // Run the over-ride hook.
            this.preLoad();

            // Load App
            await this.loadApp();

            // Run the over-ride hook.
            this.postLoad();

            return true;
        } catch (error) {
            if (error instanceof AppNotAllowedError) {
                console.error('Failed to initialize:', error);
                console.log(...Log.w(`${this.constructor.name}`, 'ERROR', `AppNotAllowedError Failed to load 1 or more apps: ${error}`));
                return false;
            }
            this.sentry.captureException(error);
            console.error('Critical Error:', error);
            return false;
        }
    }

    async loadApp() {
        const huggerCtaAppDetails = await this.appFactory.compileApp(this.config.apps.HuggerCTA);
        const huggerRecommendationApp = await this.appFactory.compileApp(this.config.apps.HuggerRecommendation);

        this.huggerCtaApp = new HuggerCTA(huggerCtaAppDetails);
        const huggerCtaPromise = this.huggerCtaApp.init();

        this.huggerRecommendationWindowApp = new HuggerRecommendation(huggerRecommendationApp);
        const huggerRecommendationWindowPromise = this.huggerRecommendationWindowApp.init();

        // Set the huggerCtaApps Displayer to the instance of the window
        this.huggerCtaApp.appToDisplay = this.huggerRecommendationWindowApp;

        // Build a connection between the apps, similar to an event emitter system.
        this.huggerRecommendationWindowApp.connections.huggerCtaApp = this.huggerCtaApp;
        this.huggerCtaApp.connections.huggerRecommendation = this.huggerRecommendationWindowApp;

        // Do not continue until both apps have loaded.
        // !! Does this app need each other at loading time?
        // await Promise.all([huggerCtaAppDetails, huggerRecommendationApp]);

        // Link any errors from init, to the error handler.
        // Wait for both init promises to settle
        Promise.allSettled([huggerCtaPromise, huggerRecommendationWindowPromise]).then((results) => {
            results.forEach((result) => {
                if (result.status === 'rejected') {
                    const error = new Error(result.reason.message);
                    error.stack = result.reason.stack;
                    this.handleError(error);
                }
            });
        });
        return true;
    }
}
